import React, { useEffect, useState } from "react";
import { FilterRange } from "./types";
import {
  parseRangeValues,
  validateRangeValues,
  formatRangeValue,
} from "./utils";
import Stack from "@mui/material/Stack";
import { RangeTextField } from "./RangeTextField";

export interface RangeFilterProps {
  value: FilterRange;
  onChange: (min: number | undefined, max: number | undefined) => void;
  minLabel?: string;
  maxLabel?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  isDisabled?: boolean;
  prefix?: string;
  thousandSeparator?: boolean | string;
  maxNumericValue?: number;
  updatedOnType?: boolean;
}

/**
 * A component for handling range inputs with min and max values
 */
export const RangeFilter = ({
  value,
  onChange,
  minLabel = "Min",
  maxLabel = "Max",
  minPlaceholder = "No limit",
  maxPlaceholder = "No limit",
  isDisabled = false,
  prefix = "",
  thousandSeparator = true,
  updatedOnType = false,
  maxNumericValue = 999_999_999_999_999,
}: RangeFilterProps) => {
  const [minValue, setMinValue] = useState<string>(
    formatRangeValue(value?.min)
  );
  const [maxValue, setMaxValue] = useState<string>(
    formatRangeValue(value?.max === 0 ? undefined : value?.max)
  );
  const [minError, setMinError] = useState<string>("");
  const [maxError, setMaxError] = useState<string>("");

  // Update local state when value prop changes
  useEffect(() => {
    setMinValue(formatRangeValue(value?.min));
    setMaxValue(formatRangeValue(value?.max === 0 ? undefined : value?.max));
  }, [value]);

  const validateAndUpdateRange = (min: string, max: string, update = false) => {
    const { minError, maxError } = validateRangeValues(min, max);
    setMinError(minError);
    setMaxError(maxError);

    // Only update the value if there are no errors and update is true
    if (minError === "" && maxError === "" && update) {
      const range = parseRangeValues(min, max);
      onChange(range.min, range.max);
    }
  };
  const handleInputStart = (
    newValue: string,
    setValue: React.Dispatch<React.SetStateAction<string>>,
    otherValue: string,
    flag: boolean
  ) => {
    setValue(newValue);
    validateAndUpdateRange(
      setValue === setMinValue ? newValue : otherValue,
      setValue === setMaxValue ? newValue : otherValue,
      flag
    );
  };
  const onMinInputStart = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputStart(e.target.value, setMinValue, maxValue, updatedOnType);
  };
  const onMaxInputStart = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputStart(e.target.value, setMaxValue, minValue, updatedOnType);
  };

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputStart(e.target.value, setMinValue, maxValue, true);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputStart(e.target.value, setMaxValue, minValue, true);
  };

  return (
    <Stack direction="row" spacing={2}>
      <RangeTextField
        label={minLabel}
        placeholder={minPlaceholder}
        value={minValue}
        onChange={handleMinChange}
        onInput={onMinInputStart}
        error={minError}
        isDisabled={isDisabled}
        prefix={prefix}
        thousandSeparator={thousandSeparator}
        maxValue={maxNumericValue}
      />
      <RangeTextField
        label={maxLabel}
        placeholder={maxPlaceholder}
        value={maxValue}
        onChange={handleMaxChange}
        onInput={onMaxInputStart}
        error={maxError}
        isDisabled={isDisabled}
        prefix={prefix}
        thousandSeparator={thousandSeparator}
        maxValue={maxNumericValue}
      />
    </Stack>
  );
};
